import { supabase } from "@/integrations/supabase/client";
import { legalConfig } from "@/config/legalConfig";
import { eventBus } from "@/lib/eventBus";

type ConsentType =
  | 'data_processing'
  | 'marketing'
  | 'notifications'
  | 'analytics'
  | 'third_party_sharing'
  | 'health_data' // RGPD Art. 9 — consentement santé (HealthDataConsentModal)
  | 'fmd_gps_tracking' // FMD V1A — RGPD Art. 7, collecte GPS trajets covoit
  | 'fmd_carpooling_participation' // FMD V1A — AIPD §3, opt-in volontaire module
  | 'fmd_trip_authenticity' // FMD V1A — Art. R.3261-13-2 Code travail, déclaration per-trip
  | 'photo_food_analysis'; // Analyse d'aliments par photo (objectifs alimentation)

interface ConsentMetadata {
  ip_address?: string;
  user_agent?: string;
  consent_version?: string;
  source?: string;
  /** Option distincte : afficher ou non l'estimation d'énergie du repas. */
  energy_estimate?: boolean;
}

const OPTIONAL_CONSENT_DURATION_MONTHS = 13;

export const consentService = {
  /**
   * Enregistrer un consentement dans Supabase
   */
  async recordConsent(
    userId: string,
    consentType: ConsentType,
    consentGiven: boolean,
    metadata?: ConsentMetadata
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const consentVersion = legalConfig.lastUpdate.privacyPolicy;

      // ✅ ANTI-DOUBLON : vérifier si un consentement identique existe déjà avant d'insérer
      // Évite la race condition lors de l'onboarding (multiples appels parallèles en < 1s)
      const { data: existing } = await supabase
        .from('consent_logs')
        .select('id')
        .eq('user_id', userId)
        .eq('consent_type', consentType as any)
        .eq('consent_given', consentGiven)
        .eq('consent_version', consentVersion)
        .is('revoked_at', null)
        .limit(1)
        .maybeSingle();

      if (existing) {
        // Consentement identique déjà actif — retourner succès sans dupliquer
        console.info(`[ConsentService] Consentement ${consentType} déjà enregistré pour ${userId.slice(0, 8)}…, skip`);
        return { success: true };
      }

      const expiresAt = consentType === 'data_processing' ? null : new Date();
      expiresAt?.setMonth(expiresAt.getMonth() + OPTIONAL_CONSENT_DURATION_MONTHS);

      const { error } = await supabase
        .from('consent_logs')
        .insert({
          user_id: userId,
          consent_type: consentType as any,
          consent_given: consentGiven,
          consent_version: consentVersion,
          expires_at: expiresAt?.toISOString() ?? null,
          renewal_type: 'initial',
          metadata: metadata ? {
            ...metadata,
            user_agent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
          } : {
            user_agent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
          },
        });

      if (error) {
        console.error('Error recording consent:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error recording consent:', error);
      return { success: false, error: 'Une erreur est survenue lors de l\'enregistrement du consentement' };
    }
  },

  /**
   * Enregistrer les consentements d'inscription (CGU + Privacy).
   *
   * RGPD Art. 7 (consentement spécifique) : les consentements FMD
   * (`fmd_gps_tracking`, `fmd_carpooling_participation`) ne sont PLUS posés
   * à l'inscription. Ils sont collectés via un opt-in dédié au premier
   * usage du module FMD (voir `recordFmdOptInConsents` +
   * `FmdConsentOptInCard`). Le retrait reste possible à tout moment
   * depuis « Mes données personnelles ».
   */
  async recordRegistrationConsents(
    userId: string,
    metadata?: ConsentMetadata
  ): Promise<{ success: boolean; error?: string }> {
    try {
      return await this.recordConsent(userId, 'data_processing', true, {
        ...metadata,
        source: 'registration',
      });
    } catch (error) {
      console.error('Error recording registration consents:', error);
      return { success: false, error: 'Erreur lors de l\'enregistrement des consentements' };
    }
  },

  /**
   * Opt-in spécifique au module FMD (RGPD Art. 7 — consentement libre,
   * spécifique, éclairé). Appelé depuis `FmdConsentOptInCard` au premier
   * usage du module, jamais à l'inscription.
   */
  async recordFmdOptInConsents(
    userId: string,
    metadata?: ConsentMetadata
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const [fmdGps, fmdOptIn] = await Promise.all([
        this.recordConsent(userId, 'fmd_gps_tracking', true, {
          ...metadata,
          source: metadata?.source ?? 'fmd_opt_in_card',
        }),
        this.recordConsent(userId, 'fmd_carpooling_participation', true, {
          ...metadata,
          source: metadata?.source ?? 'fmd_opt_in_card',
        }),
      ]);

      if (!fmdGps.success) return fmdGps;
      if (!fmdOptIn.success) return fmdOptIn;
      return { success: true };
    } catch (error) {
      console.error('Error recording FMD opt-in consents:', error);
      return { success: false, error: 'Erreur lors de l\'enregistrement du consentement FMD' };
    }
  },

  /**
   * Vérifie si l'opt-in FMD complet (GPS + participation) est actif.
   */
  async hasFmdOptIn(userId: string): Promise<boolean> {
    const [gps, participation] = await Promise.all([
      this.hasValidConsent(userId, 'fmd_gps_tracking'),
      this.hasValidConsent(userId, 'fmd_carpooling_participation'),
    ]);
    return gps && participation;
  },

  /**
   * Analyse d'aliments par photo (objectifs alimentation).
   * Deux choix distincts dans un seul enregistrement :
   *  - l'analyse elle-même (obligatoire pour ce type d'objectif) ;
   *  - l'affichage d'une fourchette d'énergie, en métadonnée, désactivé par défaut.
   * La photo n'est jamais conservée, l'employeur ne voit rien d'individuel.
   */
  async getFoodPhotoConsent(
    userId: string
  ): Promise<{ granted: boolean; energyEstimate: boolean }> {
    if (!userId || userId.trim() === '') return { granted: false, energyEstimate: false };
    try {
      const { data, error } = await supabase
        .from('consent_logs')
        .select('metadata, expires_at')
        .eq('user_id', userId)
        .eq('consent_type', 'photo_food_analysis' as any)
        .eq('consent_given', true)
        .is('revoked_at', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error || !data) return { granted: false, energyEstimate: false };
      if (data.expires_at && new Date(data.expires_at) <= new Date()) {
        return { granted: false, energyEstimate: false };
      }
      const meta = (data.metadata ?? {}) as Record<string, unknown>;
      return { granted: true, energyEstimate: meta.energy_estimate === true };
    } catch {
      return { granted: false, energyEstimate: false };
    }
  },

  /**
   * Enregistre (ou met à jour) le consentement analyse photo d'aliments.
   * On révoque d'abord l'existant : l'option d'estimation d'énergie vit dans
   * la métadonnée, un simple INSERT anti-doublon ne la mettrait pas à jour.
   */
  async recordFoodPhotoConsent(
    userId: string,
    energyEstimate: boolean,
    source = 'photo_ai_validation'
  ): Promise<{ success: boolean; error?: string }> {
    if (!userId) return { success: false, error: 'Utilisateur inconnu' };
    await this.revokeConsent(userId, 'photo_food_analysis').catch(() => undefined);
    return this.recordConsent(userId, 'photo_food_analysis', true, {
      source,
      energy_estimate: energyEstimate,
    });
  },



  /**
   * Vérifier si un consentement existe et est valide
   */
  async hasValidConsent(
    userId: string,
    consentType: ConsentType
  ): Promise<boolean> {
    // 🔒 P0.11 : Guard contre userId vide pour éviter erreurs UUID en boucle
    if (!userId || userId.trim() === '') {
      console.warn('[ConsentService] hasValidConsent called with empty userId');
      return false;
    }
    
    try {
      // ✅ CORRIGÉ: Pas de vérification préventive de session
      // La requête RLS échouera naturellement si la session est invalide
      // Évite la race condition qui causait une boucle de déconnexion
      
      const { data, error } = await supabase
        .from('consent_logs')
        .select('*')
        .eq('user_id', userId)
        .eq('consent_type', consentType as any)
        .eq('consent_given', true)
        .is('revoked_at', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        // ⚠️ Ne PLUS émettre session:invalid sur une simple erreur RLS.
        // Cause historique de déconnexions intempestives (3 reconnexions
        // d'affilée constatées 2026-05-31). Une erreur RLS ici signifie
        // simplement « pas de ligne lisible », pas « session morte ».
        // La vraie invalidation de session est détectée en amont par
        // useAuthLogic (refresh fatal) et par les guards de route.
        if (error.code === '42501' || error.message?.includes('row-level security')) {
          console.warn('[ConsentService] RLS denied on consent_logs read, treating as no consent (not a session error)');
        }
        return false;
      }
      
      if (!data) {
        return false;
      }

      // La ligne historique `data_processing` prouve l'acceptation des
      // conditions et la prise de connaissance de la politique. Ce n'est pas
      // un accord optionnel à renouveler tous les 13 mois.
      if (consentType === 'data_processing') return true;

      // Les accords réellement optionnels conservent leur durée dédiée.
      if (data.expires_at) {
        const expiresAt = new Date(data.expires_at);
        return expiresAt > new Date();
      }

      return true;
    } catch (error) {
      console.error('Error checking consent:', error);
      return false;
    }
  },

  /**
   * Révoquer un consentement
   */
  async revokeConsent(
    userId: string,
    consentType: ConsentType
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Révoquer TOUTES les lignes actives de ce type, pas seulement la plus
      // récente. L'anti-doublon de recordConsent est un SELECT puis INSERT non
      // atomique : des doublons actifs existent (3 avérés en production,
      // migration du 25/11/2025). Avec un .limit(1), une ligne survivait et
      // hasValidConsent la retrouvait : le retrait art. 7.3 était sans effet
      // réel. Même patron que la révocation push de MyData.
      const { data: revoked, error: updateError } = await supabase
        .from('consent_logs')
        .update({ revoked_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('consent_type', consentType as any)
        .eq('consent_given', true)
        .is('revoked_at', null)
        .select('id');

      if (!updateError && (!revoked || revoked.length === 0)) {
        return { success: false, error: 'Aucun consentement actif trouvé' };
      }

      if (updateError) {
        console.error('Error revoking consent:', updateError);
        return { success: false, error: updateError.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error revoking consent:', error);
      return { success: false, error: 'Erreur lors de la révocation du consentement' };
    }
  },

  /**
   * Récupérer l'historique des consentements d'un utilisateur
   */
  async getConsentHistory(userId: string, consentType?: ConsentType) {
    try {
      let query = supabase
        .from('consent_logs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (consentType) {
        query = query.eq('consent_type', consentType as any);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching consent history:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching consent history:', error);
      return [];
    }
  },

  /**
   * Récupérer les détails d'un consentement (expiration, version)
   */
  async getConsentDetails(
    userId: string,
    consentType: ConsentType
  ): Promise<{ daysUntilExpiration: number | null; consentVersion: string | null; expiresAt: string | null } | null> {
    try {
      const { data, error } = await supabase
        .from('consent_logs')
        .select('expires_at, consent_version')
        .eq('user_id', userId)
        .eq('consent_type', consentType as any)
        .eq('consent_given', true)
        .is('revoked_at', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error || !data) {
        return null;
      }

      const expiresAt = data.expires_at ? new Date(data.expires_at) : null;
      const daysUntilExpiration = expiresAt 
        ? Math.ceil((expiresAt.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
        : null;

      return {
        daysUntilExpiration,
        consentVersion: data.consent_version,
        expiresAt: data.expires_at,
      };
    } catch (error) {
      console.error('Error fetching consent details:', error);
      return null;
    }
  },

  /**
   * Enregistrer un renouvellement de consentement avec le motif
   * ✅ ORDRE INVERSÉ : D'abord INSERT le nouveau, PUIS REVOKE l'ancien
   * Évite que l'utilisateur se retrouve sans consentement si l'INSERT échoue
   */
  async recordConsentRenewal(
    userId: string,
    renewalType: 'renewal_expiration' | 'renewal_version_change',
    metadata?: ConsentMetadata
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // 1. D'ABORD insérer le nouveau consentement
      const consentVersion = legalConfig.lastUpdate.privacyPolicy;
      const expiresAt = renewalType === 'renewal_version_change' ? null : new Date();
      expiresAt?.setMonth(expiresAt.getMonth() + OPTIONAL_CONSENT_DURATION_MONTHS);

      // ✅ FIX RACE CONDITION : capturer l'id directement depuis l'INSERT
      // Évite le re-SELECT qui pouvait retourner le même id lors de deux appels parallèles (iOS double-tap)
      const { data: insertedConsent, error: insertError } = await supabase
        .from('consent_logs')
        .insert({
          user_id: userId,
          consent_type: 'data_processing',
          consent_given: true,
          consent_version: consentVersion,
          expires_at: expiresAt?.toISOString() ?? null,
          renewal_type: renewalType,
          metadata: metadata ? {
            ...metadata,
            user_agent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
          } : {
            user_agent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
          },
        })
        .select('id')
        .single();

      if (insertError) {
        console.error('Error recording consent renewal:', insertError);
        return { success: false, error: insertError.message };
      }

      // 2. SEULEMENT après succès, révoquer TOUS les anciens consentements actifs
      // Chaque appel utilise son propre insertedConsent.id — indépendant des appels concurrents
      if (insertedConsent) {
        await supabase
          .from('consent_logs')
          .update({ revoked_at: new Date().toISOString() })
          .eq('user_id', userId)
          .eq('consent_type', 'data_processing')
          .eq('consent_given', true)
          .is('revoked_at', null)
          .neq('id', insertedConsent.id);
      }

      return { success: true };
    } catch (error) {
      console.error('Error recording consent renewal:', error);
      return { success: false, error: 'Une erreur est survenue lors du renouvellement du consentement' };
    }
  },
};
